import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Plus, RefreshCw, ChevronDown } from "lucide-react";
import EmailEditorCore, { EmailEditorCoreRef } from "../../shared/email-editor-core";
import { PlainTextEditor } from "../../shared/plain-text-editor";
import { VARIABLE_OPTIONS } from "../../crm/conversation/components/floating-panel";
import { designFromHtml } from "../../shared/email-design";
// import { EmailMockUp } from "../campaign/email-mock"; // ← your actual path

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EmailFormData {
  subject: string;
  previewText: string;
  senderEmail: string;
  senderName: string;
  plainText: string;
  htmlContent: string;
  variables: { name: string; defaultValue: string }[];
  design?: object | null;
}

interface EmailformProps {
  formData?: Partial<EmailFormData>;
  onChange?: (updates: Partial<EmailFormData>) => void;
  readOnly?: boolean;
}

// ─── Default Variables Modal ──────────────────────────────────────────────────

const DefaultVariablesModal: React.FC<{
  variables: { name: string; defaultValue: string }[];
  onSave: (vars: { name: string; defaultValue: string }[]) => void;
  onClose: () => void;
}> = ({ variables, onSave, onClose }) => {
  const [rows, setRows] = useState(
    variables.length > 0 ? variables : [{ name: "", defaultValue: "" }]
  );

  const update = (i: number, field: "name" | "defaultValue", val: string) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-[500px] overflow-hidden">
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Edit default variables</h3>
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1 max-w-sm leading-relaxed">
              Edit fallback values for variables that may be missing in your contact data.
              If a default value isn't provided, the variable will be automatically removed from the message.
            </p>
          </div>
          <button onClick={onClose} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ml-4 flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3 max-h-64 overflow-y-auto">
          <div className="grid grid-cols-[1fr_24px_1fr_24px] gap-2 items-center">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Variable Names</span>
            <span />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Replace with</span>
            <span />
          </div>

          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_24px_1fr_24px] gap-2 items-center">
              <div className="relative">
                <select
                  value={row.name}
                  onChange={(e) => update(i, "name", e.target.value)}
                  className="w-full appearance-none text-sm border border-gray-200 dark:border-gray-600 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 pr-7 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                >
                  <option value="">Select variable</option>
                  {VARIABLE_OPTIONS.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
                <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
              </div>
              <span className="text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm text-center">→</span>
              <div className="relative">
                <input
                  value={row.defaultValue}
                  onChange={(e) => update(i, "defaultValue", e.target.value)}
                  placeholder={`e.g., "Customer"`}
                  className="w-full text-sm border border-gray-200 dark:border-gray-600 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 pr-7 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                />
                <button
                  onClick={() => update(i, "defaultValue", "")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400"
                >
                  <RefreshCw size={12} />
                </button>
              </div>
              <button
                onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
                className="text-gray-300 dark:text-gray-600 hover:text-red-400 transition-colors flex items-center justify-center"
              >
                <X size={14} />
              </button>
            </div>
          ))}

          {rows[0]?.name && (
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">
              e.g., "Customer" when {rows[0].name} is unavailable
            </p>
          )}

          <button
            onClick={() => setRows((prev) => [...prev, { name: "", defaultValue: "" }])}
            className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
          >
            <Plus size={13} />
            Add New
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 dark:border-gray-800">
          <button onClick={onClose} className="text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg px-4 py-2 transition-colors">
            Discard
          </button>
          <button
            onClick={() => { onSave(rows.filter((r) => r.name.trim())); onClose(); }}
            className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-5 py-2 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};


const DEFAULT_DATA: EmailFormData = {
  subject: "", previewText: "", senderEmail: "", senderName: "",
  plainText: "", htmlContent: "", variables: [],
};

type TabMode = "plain" | "editor";

const Emailform: React.FC<EmailformProps> = ({
  formData: ext,
  onChange,
  readOnly = false,
}) => {
  const [local, setLocal] = useState<EmailFormData>(DEFAULT_DATA);
  const [activeTab, setActiveTab] = useState<TabMode>("plain");
  const [showVarsModal, setShowVarsModal] = useState(false);
  const editorRef = useRef<EmailEditorCoreRef | null>(null);

  const d: EmailFormData = { ...DEFAULT_DATA, ...local, ...ext };

  const set = (updates: Partial<EmailFormData>) => {
    setLocal((prev) => ({ ...prev, ...updates }));
    onChange?.(updates);
  };

  // A saved design reopens block-for-block; HTML without one (older
  // templates) is rebuilt into blocks from the HTML (designFromHtml).
  // Loaded once per editor mount — the template's data can arrive after
  // mount, and later design updates are the editor's own edits coming back.
  const [editorReady, setEditorReady] = useState(false);
  const loadedRef = useRef(false);
  const hasSaved = !!d.design || !!d.htmlContent;
  const isEditorEmail = !!d.design || /^\s*(<!doctype|<html)/i.test(d.htmlContent);

  useEffect(() => {
    if (loadedRef.current || !hasSaved) return;
    if (isEditorEmail && activeTab === "plain" && !d.plainText) setActiveTab("editor");
    if (!editorReady) return;
    loadedRef.current = true;
    const design = d.design || designFromHtml(d.htmlContent);
    if (!design) return;
    const t = setTimeout(() => editorRef.current?.loadDesign(design as any), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorReady, hasSaved]);

  // The editor unmounts on the Plain Text tab; its next mount reloads
  // whatever is latest (d.design tracks every edit).
  useEffect(() => {
    if (activeTab === "editor") return;
    setEditorReady(false);
    loadedRef.current = false;
  }, [activeTab]);

  const handleEditorReady = useCallback(() => setEditorReady(true), []);

  const handleTemplateGenerated = useCallback(
    (data: { html: string; subject?: string; header?: string; plainText?: string }) => {
      set({
        htmlContent: data.html,
        ...(data.subject ? { subject: data.subject } : {}),
        ...(data.header ? { previewText: data.header } : {}),
      });
    },
    []
  );

  return (
    <>
      <div className="space-y-4">



        {/* ── Subject + Preview Text ──────────────────────────────────────── */}
        <div className="space-y-0 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          {/* Subject row */}
          <div className="flex items-start gap-4 px-4 py-3">
            <div className="w-28 shrink-0 pt-0.5">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Subject</span>
              <div className={`text-xs mt-0.5 ${d.subject.length > 70 ? "text-red-500" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}`}>
                {d.subject.length}/70
              </div>
            </div>
            <input
              value={d.subject}
              onChange={(e) => set({ subject: e.target.value })}
              maxLength={70}
              readOnly={readOnly}
              placeholder="Enter a subject"
              className="flex-1 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none bg-transparent py-0.5"
            />
          </div>

          {/* Preview Text row */}
          <div className="flex items-start gap-4 px-4 py-3">
            <div className="w-28 shrink-0 pt-0.5">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Preview Text</span>
              <div className={`text-xs mt-0.5 ${d.previewText.length > 70 ? "text-red-500" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}`}>
                {d.previewText.length}/70
              </div>
            </div>
            <input
              value={d.previewText}
              onChange={(e) => set({ previewText: e.target.value })}
              maxLength={70}
              readOnly={readOnly}
              placeholder="Enter a preview text"
              className="flex-1 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none bg-transparent py-0.5"
            />
          </div>
        </div>

        {/* ── Tab switcher ────────────────────────────────────────────────── */}
        <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-indigo-50/60 dark:bg-indigo-500/10 p-1 -z-50 gap-1">
          {(["plain", "editor"] as TabMode[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${
                activeTab === tab
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              {tab === "plain" ? "Plain Text" : "Email Editor"}
            </button>
          ))}
        </div>

        {/* ── Editor area ─────────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-h-0">
          {activeTab === "plain" && (
            // PlainTextEditor's onChange is contentEditable innerHTML (real
            // markup, not literal plain text) — keep htmlContent in sync so
            // the backend (which requires html_content, not plain_text) has
            // something to send even if the user never opens the Email
            // Editor tab.
            <PlainTextEditor value={d.plainText} onChange={(v) => set({ plainText: v, htmlContent: v, design: null })} />
          )}
          {activeTab === "editor" && (
            <div className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden" style={{ minHeight: 500 }}>
              <EmailEditorCore
                ref={editorRef}
                minHeight="500px"
                onReady={handleEditorReady}
                onChange={(html, design) => set({ htmlContent: html, design: design ?? null })}
                variables={d.variables}
                onVariablesChange={(vars) => set({ variables: vars })}
                onTemplateGenerated={handleTemplateGenerated}
              />
            </div>
          )}
        </div>

        {/* ── Footer toolbar ───────────────────────────────────────────────── */}
        {/* Email Editor has its own Add Variables beside ConvoAI — one button per tab. */}
        {activeTab === "plain" && (
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={() => setShowVarsModal(true)}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 border border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-600 rounded-lg px-3 py-1.5 transition-colors"
          >
            <Plus size={13} />
            Add variable
          </button>
        </div>
        )}
      </div>

      {showVarsModal && (
        <DefaultVariablesModal
          variables={d.variables}
          onSave={(vars) => set({ variables: vars })}
          onClose={() => setShowVarsModal(false)}
        />
      )}
    </>
  );
};

export default Emailform;